/**
 * The BOARD, as geometry.
 *
 * Everything in this file is presentation arithmetic — where a card sits on the
 * table and what flag is flying. It computes NO rules. Every number it consumes
 * (a running order, a caution clock, a scheduled length) is handed to it by the
 * engine; it only decides where to draw things.
 *
 * The arrangement is the physical game's own, confirmed from Boards A, B and D
 * (see `src/domain/types.ts` and the project's RAW_boards_A_B record):
 *
 *     LEADER      1st          — one card, alone, elevated
 *     TOP         2nd-7th      — six boxes, one card each (Board A)
 *     MIDDLE      8th-19th     — six boxes of TWO, ODD left / EVEN right (Board B)
 *     BOTTOM      20th back    — a face-up stack (Board D)
 *
 * The MIDDLE pairing is not invented here: it is read straight off
 * `MIDDLE_BOX_POSITION` in the challenge module, which is what the engine uses
 * to resolve a white-die box selection. If the board ever disagrees with the
 * rules, it is because the rules changed, and the board follows.
 */

import type { Car, Group } from '../domain/types.ts';
import { groupOf } from '../domain/types.ts';
import { MIDDLE_BOX_POSITION } from '../domain/challenge.ts';

// ---------------------------------------------------------------------------
// The table, in board units (px at scale 1)
// ---------------------------------------------------------------------------

export const CARD_W = 118;
export const CARD_H = 152;
export const LEADER_W = 150;
export const LEADER_H = 192;

/** The three boards laid out on the left; the BOTTOM stack on its own rail. */
export const BOARD_LEFT_W = 830;
export const BOARD_W = 1040;
export const BOARD_H = 870;

/** Each board carries a printed heading; this is the band it sits in. */
export const HEADING_H = 26;

export const LEADER_X = (BOARD_LEFT_W - LEADER_W) / 2;
export const LEADER_Y = 40;

export const TOP_GAP = 12;
export const TOP_ROW_W = 6 * CARD_W + 5 * TOP_GAP;
export const TOP_X0 = (BOARD_LEFT_W - TOP_ROW_W) / 2;
export const TOP_Y = 262;

export const BOX_PAD = 10;
export const BOX_INNER_GAP = 10;
export const BOX_LABEL_H = 24;
export const BOX_W = 2 * CARD_W + BOX_INNER_GAP + 2 * BOX_PAD;
export const BOX_H = BOX_LABEL_H + CARD_H + BOX_PAD;
export const BOX_COL_GAP = 16;
export const BOX_ROW_GAP = 16;
export const MIDDLE_Y = 454;

export const RAIL_X = BOARD_LEFT_W + 44;
export const RAIL_Y = 66;
/** How far the first few stack cards are fanned — enough to read number + name. */
export const STACK_FAN = 46;
/** …and how far the rest are, so the tail reads as a mass, not a list. */
export const STACK_PACK = 14;
/** Cards fanned wide enough to be legible before the stack compresses. */
export const STACK_LEGIBLE = 6;

/** One turn is ten miles (rulebook p.ii). Used only for the booth's lap readout. */
export const MILES_PER_TURN = 10;

// ---------------------------------------------------------------------------
// Slots
// ---------------------------------------------------------------------------

export interface Slot {
  /** 1-based running-order position this slot holds. */
  position: number;
  group: Group;
  x: number;
  y: number;
  w: number;
  h: number;
  z: number;
  /** 0 for a board slot; index into the BOTTOM stack otherwise. */
  depth: number;
  /** 1-6 for TOP and MIDDLE board boxes; null in the stack. */
  box: number | null;
  /** Which half of a MIDDLE box, per Board B's ODD/EVEN printing. */
  half: 'odd' | 'even' | null;
}

/** position -> the MIDDLE box and half that hold it, read off the engine's table. */
const MIDDLE_SLOTS = new Map<number, { box: number; half: 'odd' | 'even' }>();
for (const [box, pair] of Object.entries(MIDDLE_BOX_POSITION)) {
  MIDDLE_SLOTS.set(pair.odd, { box: Number(box), half: 'odd' });
  MIDDLE_SLOTS.set(pair.even, { box: Number(box), half: 'even' });
}

/** The six MIDDLE boxes in board order, front of the field first. */
export const MIDDLE_BOXES: readonly { box: number; odd: number; even: number }[] = Object.entries(
  MIDDLE_BOX_POSITION,
)
  .map(([box, pair]) => ({ box: Number(box), odd: pair.odd, even: pair.even }))
  .sort((a, b) => a.box - b.box);

export function middleBoxOrigin(box: number): { x: number; y: number } {
  const col = (box - 1) % 3;
  const row = Math.floor((box - 1) / 3);
  return {
    x: col * (BOX_W + BOX_COL_GAP),
    y: MIDDLE_Y + row * (BOX_H + BOX_ROW_GAP),
  };
}

/** How far down the rail the nth card of the BOTTOM stack sits. */
export function stackOffset(depth: number): number {
  return depth <= STACK_LEGIBLE
    ? depth * STACK_FAN
    : STACK_LEGIBLE * STACK_FAN + (depth - STACK_LEGIBLE) * STACK_PACK;
}

/**
 * Where the card running in `position` belongs on the table.
 *
 * Throws on a non-positive position — a running order is 1..n with no gaps, and
 * a hole here would mean the engine handed us something it promises it never
 * does (see `fieldFromState`).
 */
export function slotFor(position: number): Slot {
  if (!Number.isInteger(position) || position < 1) {
    throw new RangeError(`a running-order position must be a positive integer, got ${position}`);
  }
  const group = groupOf(position);

  if (group === 'leader') {
    return {
      position, group,
      x: LEADER_X, y: LEADER_Y, w: LEADER_W, h: LEADER_H,
      z: 900, depth: 0, box: null, half: null,
    };
  }

  if (group === 'top') {
    const box = position - 1; // 2nd place is box 1 on Board A
    return {
      position, group,
      x: TOP_X0 + (box - 1) * (CARD_W + TOP_GAP),
      y: TOP_Y, w: CARD_W, h: CARD_H,
      z: 800, depth: 0, box, half: null,
    };
  }

  if (group === 'middle') {
    const seat = MIDDLE_SLOTS.get(position);
    if (!seat) throw new RangeError(`no MIDDLE box holds position ${position}`);
    const origin = middleBoxOrigin(seat.box);
    return {
      position, group,
      x: origin.x + BOX_PAD + (seat.half === 'odd' ? 0 : CARD_W + BOX_INNER_GAP),
      y: origin.y + BOX_LABEL_H,
      w: CARD_W, h: CARD_H,
      z: 700, depth: 0, box: seat.box, half: seat.half,
    };
  }

  const depth = position - 20;
  return {
    position, group,
    x: RAIL_X + (depth % 2) * 5,
    y: RAIL_Y + stackOffset(depth),
    w: CARD_W, h: CARD_H,
    // Front of the stack sits on top; the tail recedes behind it.
    z: 600 - depth,
    depth, box: null, half: null,
  };
}

/** Every board slot that is printed on the table, filled or not. */
export const PRINTED_SLOTS: readonly Slot[] = Array.from({ length: 19 }, (_, i) => slotFor(i + 1));

// ---------------------------------------------------------------------------
// Bucketing a running order
// ---------------------------------------------------------------------------

export interface Boards {
  leader: Car | null;
  top: Car[];
  middle: Car[];
  bottom: Car[];
}

/**
 * Split a running order into the four boards. The order is taken as the engine
 * gives it — sorted by `position`, never renumbered here.
 */
export function bucketField(cars: readonly Car[]): Boards {
  const order = [...cars].sort((a, b) => a.position - b.position);
  const boards: Boards = { leader: null, top: [], middle: [], bottom: [] };
  for (const car of order) {
    const group = groupOf(car.position);
    if (group === 'leader') boards.leader = car;
    else if (group === 'top') boards.top.push(car);
    else if (group === 'middle') boards.middle.push(car);
    else boards.bottom.push(car);
  }
  return boards;
}

// ---------------------------------------------------------------------------
// The flag stand
// ---------------------------------------------------------------------------

export type Flag = 'pre-race' | 'green' | 'yellow' | 'white' | 'checkered';

export interface FlagInput {
  /** Has a turn been run yet? */
  started: boolean;
  /** Has the checkered fallen — i.e. are we at the end of the engine's turn list? */
  finished: boolean;
  /** `RaceState.yellowTurnsRemaining` after the last turn played. */
  yellowTurnsRemaining: number;
  /** `RaceState.turn` after the last turn played — the turn about to be run. */
  nextTurn: number;
  /** `RaceResult.scheduledTurns`. */
  scheduledTurns: number;
}

/**
 * Which flag is out.
 *
 * The precedence is the flag stand's own: the checkered ends everything; a
 * caution outranks the white; the white is shown when the next turn is the last
 * scheduled one. Note that a race CANNOT end under yellow (p.xv) — so a yellow
 * showing while `nextTurn > scheduledTurns` is exactly the extra-turn case, and
 * it stays yellow rather than white.
 */
export function flagFor(input: FlagInput): Flag {
  if (input.finished) return 'checkered';
  if (!input.started) return 'pre-race';
  if (input.yellowTurnsRemaining > 0) return 'yellow';
  if (input.nextTurn >= input.scheduledTurns) return 'white';
  return 'green';
}

export const FLAG_LABEL: Record<Flag, string> = {
  'pre-race': 'PRE-RACE',
  green: 'GREEN',
  yellow: 'CAUTION',
  white: 'WHITE',
  checkered: 'CHECKERED',
};

/** Scheduled turns still to run. Extra turns forced by a late caution read as 0. */
export function turnsRemaining(nextTurn: number, scheduledTurns: number): number {
  return Math.max(0, scheduledTurns - nextTurn + 1);
}

/**
 * The booth's lap readout.
 *
 * DERIVED, NOT ENGINE STATE. The engine's clock is turns, and one turn is ten
 * miles (p.ii); laps are that distance over the track's printed length. Shown
 * because a broadcast counts laps, but the authoritative number on screen is
 * always the turn count beside it.
 */
export function lapsRemaining(turns: number, lengthMiles: number | null | undefined): number | null {
  if (!lengthMiles || lengthMiles <= 0) return null;
  return Math.max(0, Math.round((turns * MILES_PER_TURN) / lengthMiles));
}

// ---------------------------------------------------------------------------
// Small shared readers
// ---------------------------------------------------------------------------

/** A driver's position in a running order, or null if he is not in it. */
export function positionIn(cars: readonly Car[], driverId: string): number | null {
  const car = cars.find((c) => c.card.driverId === driverId);
  return car ? car.position : null;
}

/** Ordinal for a place: 1st, 2nd, 3rd, 4th, 11th, 21st… */
export function ordinal(n: number): string {
  const rem100 = n % 100;
  if (rem100 >= 11 && rem100 <= 13) return `${n}th`;
  const rem10 = n % 10;
  if (rem10 === 1) return `${n}st`;
  if (rem10 === 2) return `${n}nd`;
  if (rem10 === 3) return `${n}rd`;
  return `${n}th`;
}
